/**
 * Single-user-per-device model. When a different user signs in on this device (or on logout),
 * all local data of the previous user is wiped: persisted queries, Dexie stores (base 'kgr' with
 * the outbox and the song tabs, plus every feature DB in LOCAL_DB_NAMES), offline packs and the
 * recent-songs cache. Downloads in flight are aborted and pending tab writes dropped first so
 * nothing is written after the wipe.
 *
 * A session that ends WITHOUT a logout (admin deactivation, lost-iPad revoke, "skrá út alls staðar"
 * on another device, password reset, expiry) is noticed on the next 401 from the server: then every
 * copy of choir data is removed as well (wipeRevokedData), but the user's own unsent writes (the
 * calendar outbox and queued annotation edits) and the last-user marker stay, so the same user
 * signing in again sends them, while a different user still triggers the full wipe below. That
 * member downloads their offline packs again after signing in.
 *
 * Other tabs of the app (same origin, e.g. two Safari tabs) are told about every wipe through the
 * 'kgr-auth' BroadcastChannel (fallback: the 'storage' event of the last-user key): they drop their
 * in-memory queries without writing them back and reload (main.tsx).
 */
import { del } from 'idb-keyval';
import { LAST_USER_KEY, PERSIST_KEY, queryClient } from '../api/queryClient';
import { resetOutboxMemory } from '../sync/outbox';
import { resetTabsMemory } from '../tabs/store';
import { stopOfflineRuntime } from './manager';
import { CONTENT_CACHE, OFFLINE_DB_NAME, RECENT_CACHE } from './types';

/** localStorage: the user whose choir data was already removed from this device (cleared on sign-in). */
const WIPED_USER_KEY = 'kgr.wipedUserId';
const AUTH_CHANNEL = 'kgr-auth';
const WIPE_MESSAGE = 'wipe';

/** IndexedDB databases owned by features (besides the base 'kgr' DB and the query cache). */
export const LOCAL_DB_NAMES = ['kgr-annotations', 'kgr-offline'] as const;

/* ─── localStorage (may be unavailable: undefined) ──────────────────────── */

function readLs(key: string): string | null | undefined {
  try {
    return localStorage.getItem(key);
  } catch {
    return undefined;
  }
}

function writeLs(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/* ─── Cross-tab signal ──────────────────────────────────────────────────── */

let channel: BroadcastChannel | null | undefined;

/** One channel object per page: a BroadcastChannel never delivers a message to the object that sent it. */
function authChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    try {
      channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(AUTH_CHANNEL);
    } catch {
      channel = null;
    }
  }
  return channel;
}

function announceWipe(): void {
  try {
    authChannel()?.postMessage(WIPE_MESSAGE);
  } catch {
    /* ignore */
  }
}

/**
 * Runs `fn` (once) when another tab of the app wiped the local data or a different user signed in
 * there. Returns an unsubscribe function.
 */
export function onLocalDataWipedElsewhere(fn: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let fired = false;
  const run = () => {
    if (fired) return;
    fired = true;
    fn();
  };
  const ch = authChannel();
  const onMessage = (e: MessageEvent) => {
    if (e.data === WIPE_MESSAGE) run();
  };
  // Fallback for engines without BroadcastChannel: another user signed in elsewhere.
  const onStorage = (e: StorageEvent) => {
    if (e.key === LAST_USER_KEY && e.newValue !== e.oldValue) run();
  };
  ch?.addEventListener('message', onMessage);
  window.addEventListener('storage', onStorage);
  return () => {
    ch?.removeEventListener('message', onMessage);
    window.removeEventListener('storage', onStorage);
  };
}

/* ─── Wipes ─────────────────────────────────────────────────────────────── */

async function deleteContentCaches(): Promise<void> {
  try {
    const names = await caches.keys();
    const prefixes = [CONTENT_CACHE.replace(/-v\d+$/, ''), RECENT_CACHE.replace(/-v\d+$/, '')];
    await Promise.all(names.filter((n) => prefixes.some((p) => n.startsWith(p))).map((n) => caches.delete(n)));
  } catch {
    /* Cache API unavailable */
  }
}

async function deleteDb(name: string): Promise<void> {
  try {
    const { default: Dexie } = await import('dexie');
    await Dexie.delete(name);
  } catch {
    /* ignore */
  }
}

/** A revoked-session wipe in progress (the full wipe and sign-in wait for it). */
let revoking: Promise<void> | null = null;
/** Without localStorage the marker cannot be kept: one revoked-session wipe per page load. */
let revokedThisPage = false;

export async function wipeLocalData(): Promise<void> {
  await revoking?.catch(() => undefined);
  // Stop offline downloads / forget in-memory offline, outbox and song-tab state before deleting their stores.
  stopOfflineRuntime();
  resetOutboxMemory();
  resetTabsMemory();
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
  try {
    await del(PERSIST_KEY);
  } finally {
    await deleteContentCaches();
    try {
      const { deleteLocalDb } = await import('../db/dexie');
      await deleteLocalDb();
    } catch {
      /* not created yet */
    }
    // Feature databases (each feature owns its own Dexie DB). Keep this list in sync.
    for (const name of LOCAL_DB_NAMES) await deleteDb(name);
    // Nothing of the last user is left: a 401 arriving after this has nothing more to remove.
    const last = readLs(LAST_USER_KEY);
    if (last) writeLs(WIPED_USER_KEY, last);
    announceWipe();
  }
}

/**
 * The server says the session is gone (401 'unauthorized', not a wrong password): remove every copy
 * of choir data from this device — persisted queries, offline packs, the recent-songs cache and the
 * annotation layers pulled from the server — but keep the user's own unsent writes and the
 * last-user marker (see the module comment). Runs once per lost session (also across tabs); never rejects.
 */
export function wipeRevokedData(): Promise<void> {
  if (revoking) return revoking;
  const last = readLs(LAST_USER_KEY);
  // Nobody ever signed in here (localStorage readable, no marker): no user's data to remove.
  if (last === null || last === '') return Promise.resolve();
  const tracked = last !== undefined;
  if (tracked ? readLs(WIPED_USER_KEY) === last : revokedThisPage) return Promise.resolve();
  revokedThisPage = true;
  const run = (async () => {
    stopOfflineRuntime();
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    try {
      await del(PERSIST_KEY);
    } catch {
      /* IndexedDB unavailable: nothing was persisted */
    }
    await deleteContentCaches();
    await deleteDb(OFFLINE_DB_NAME);
    try {
      const { forgetPulledAnnotations } = await import('../annotations/local');
      await forgetPulledAnnotations();
    } catch (e) {
      console.warn('annotation copies could not be removed', e);
    }
    if (tracked) {
      writeLs(WIPED_USER_KEY, last);
      // Other tabs still hold this session's queries in memory (and would write them back).
      announceWipe();
    }
  })()
    .catch((e: unknown) => console.warn('local data wipe failed', e))
    .finally(() => {
      if (revoking === run) revoking = null;
    });
  revoking = run;
  return run;
}

export async function ensureSingleUser(userId: string): Promise<void> {
  await revoking?.catch(() => undefined);
  const last = readLs(LAST_USER_KEY);
  try {
    // A different user: the wipe also tells the other tabs (they drop the previous user's queries).
    if (last && last !== userId) await wipeLocalData();
  } finally {
    // Also when part of the wipe failed (IndexedDB unavailable: nothing was stored there either).
    writeLs(LAST_USER_KEY, userId);
    // Signed in (again): a later lost session has this user's new data to remove.
    writeLs(WIPED_USER_KEY, null);
    revokedThisPage = false;
  }
}
